import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  CheckCircle,
  AlertTriangle,
  Loader,
  Clock,
  XCircle,
  Package,
  WifiOff,
  Ban,
} from 'lucide-react';
import { ota } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { MetricCard } from '../components/MetricCard';
import { TableSkeleton } from '../components/Skeleton';
import type { OTAJob, DeviceOTAStatus } from '../types';

// A job or execution is "stuck" if IN_PROGRESS with no update for 15 minutes
const STUCK_THRESHOLD_MS = 15 * 60 * 1000;

function isStuck(lastUpdated?: string): boolean {
  if (!lastUpdated) return false;
  return Date.now() - new Date(lastUpdated).getTime() > STUCK_THRESHOLD_MS;
}

// ── Status badge helpers ────────────────────────────────────────────

function jobStatusBadge(job: OTAJob) {
  const stuck = job.status === 'IN_PROGRESS' && isStuck(job.last_updated_at);

  if (stuck) {
    return (
      <span className="badge bg-orange-500/15 text-orange-400">
        <AlertTriangle size={10} className="mr-1" />
        Stuck
      </span>
    );
  }

  switch (job.status) {
    case 'IN_PROGRESS':
      return (
        <span className="badge bg-amber-500/15 text-amber-400">
          <Loader size={10} className="mr-1 animate-spin" />
          In Progress
        </span>
      );
    case 'COMPLETED':
      return (
        <span className="badge bg-emerald-500/15 text-emerald-400">
          <CheckCircle size={10} className="mr-1" />
          Completed
        </span>
      );
    case 'CANCELLED':
      return (
        <span className="badge bg-gray-500/15 text-gray-400">
          <XCircle size={10} className="mr-1" />
          Cancelled
        </span>
      );
    case 'DELETION_IN_PROGRESS':
      return (
        <span className="badge bg-red-500/15 text-red-400">
          <Loader size={10} className="mr-1 animate-spin" />
          Deleting
        </span>
      );
    default:
      return <span className="badge bg-gray-500/15 text-gray-400">{job.status}</span>;
  }
}

function executionStatusBadge(device: DeviceOTAStatus) {
  const { execution_status: status, is_online, last_updated } = device;

  if (!status) {
    return (
      <span className="badge bg-emerald-500/15 text-emerald-400">
        <CheckCircle size={10} className="mr-1" />
        Up to date
      </span>
    );
  }

  // Queued on an offline device — device can't receive the update yet
  if (status === 'QUEUED' && is_online === false) {
    return (
      <span className="badge bg-gray-500/15 text-gray-400">
        <WifiOff size={10} className="mr-1" />
        Pending (Offline)
      </span>
    );
  }

  // Stuck: in progress but no update in a while
  if (status === 'IN_PROGRESS' && isStuck(last_updated)) {
    return (
      <span className="badge bg-orange-500/15 text-orange-400">
        <Clock size={10} className="mr-1" />
        Stuck
      </span>
    );
  }

  switch (status) {
    case 'QUEUED':
      return (
        <span className="badge bg-blue-500/15 text-blue-400">
          <Clock size={10} className="mr-1" />
          Queued
        </span>
      );
    case 'IN_PROGRESS':
      return (
        <span className="badge bg-amber-500/15 text-amber-400">
          <Loader size={10} className="mr-1 animate-spin" />
          Updating
        </span>
      );
    case 'SUCCEEDED':
      return (
        <span className="badge bg-emerald-500/15 text-emerald-400">
          <CheckCircle size={10} className="mr-1" />
          Succeeded
        </span>
      );
    case 'FAILED':
      return (
        <span className="badge bg-red-500/15 text-red-400">
          <XCircle size={10} className="mr-1" />
          Failed
        </span>
      );
    case 'TIMED_OUT':
      return (
        <span className="badge bg-red-500/15 text-red-400">
          <Clock size={10} className="mr-1" />
          Timed Out
        </span>
      );
    case 'REJECTED':
      return (
        <span className="badge bg-red-500/15 text-red-400">
          <AlertTriangle size={10} className="mr-1" />
          Rejected
        </span>
      );
    case 'CANCELED':
      return (
        <span className="badge bg-gray-500/15 text-gray-400">
          <XCircle size={10} className="mr-1" />
          Cancelled
        </span>
      );
    default:
      return <span className="badge bg-gray-500/15 text-gray-400">{status}</span>;
  }
}

function remediationHint(device: DeviceOTAStatus): string | null {
  const { execution_status: status, is_online } = device;
  if (status === 'FAILED') return 'Check device logs. Cancel this execution and re-deploy when the device is healthy.';
  if (status === 'TIMED_OUT') return 'Device did not respond in time. Verify connectivity and retry.';
  if (status === 'REJECTED') return 'Device rejected the job. Check job document compatibility with this firmware version.';
  if (status === 'QUEUED' && is_online === false) return 'Device is offline. Update will proceed automatically once the device reconnects.';
  if (status === 'IN_PROGRESS' && isStuck(device.last_updated)) return 'No progress in 15+ minutes. Verify the device is online and the OTA process has not crashed.';
  return null;
}

// ── Page component ──────────────────────────────────────────────────

export default function OTAPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [cancellingJob, setCancellingJob] = useState<string | null>(null);
  const [cancellingDevice, setCancellingDevice] = useState<string | null>(null);
  const [cancelJobError, setCancelJobError] = useState<string | null>(null);
  const [cancelDeviceError, setCancelDeviceError] = useState<string | null>(null);

  const canCancel = user?.org_type === 'kinetica' || user?.org_type === 'integrator';

  const { data: jobs = [], isLoading: loadingJobs, error: jobsError } = useQuery({
    queryKey: ['ota', 'jobs'],
    queryFn: ota.listJobs,
    refetchInterval: 30_000,
  });

  const { data: deviceStatuses = [], isLoading: loadingStatus, error: statusError } = useQuery({
    queryKey: ['ota', 'status'],
    queryFn: ota.listDeviceStatus,
    refetchInterval: 30_000,
  });

  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => ota.cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ota', 'jobs'] });
      setCancellingJob(null);
      setCancelJobError(null);
    },
    onError: (err: Error) => {
      setCancellingJob(null);
      setCancelJobError(err.message || 'Failed to cancel job');
    },
  });

  const cancelDeviceMutation = useMutation({
    mutationFn: (serial: string) => ota.cancelDeviceExecution(serial),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ota', 'status'] });
      setCancellingDevice(null);
      setCancelDeviceError(null);
    },
    onError: (err: Error) => {
      setCancellingDevice(null);
      setCancelDeviceError(err.message || 'Failed to cancel execution');
    },
  });

  const error = (jobsError || statusError) as Error | null;
  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load OTA data</p>
        <p className="text-xs text-gray-500">{error.message}</p>
        <button onClick={() => window.location.reload()} className="btn-primary mt-4 text-sm">
          Reload Page
        </button>
      </div>
    );
  }

  const jobsList = Array.isArray(jobs) ? (jobs as OTAJob[]) : [];
  const deviceStatusList = Array.isArray(deviceStatuses) ? (deviceStatuses as DeviceOTAStatus[]) : [];

  const activeJobs = jobsList.filter((j) => j.status === 'IN_PROGRESS');
  const stuckJobs = activeJobs.filter((j) => isStuck(j.last_updated_at));
  const devicesUpdating = deviceStatusList.filter(
    (a) => a.execution_status === 'IN_PROGRESS' || a.execution_status === 'QUEUED',
  );
  const devicesFailed = deviceStatusList.filter(
    (a) => a.execution_status === 'FAILED' || a.execution_status === 'TIMED_OUT' || a.execution_status === 'REJECTED',
  );
  const devicesUpToDate = deviceStatusList.filter((a) => !a.execution_status);

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">OTA Updates</h2>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Active Jobs"
          value={activeJobs.length}
          accent={activeJobs.length > 0 ? 'text-amber-400' : 'text-white'}
        />
        <MetricCard
          label="Devices Updating"
          value={devicesUpdating.length}
          accent={devicesUpdating.length > 0 ? 'text-amber-400' : 'text-white'}
        />
        <MetricCard
          label="Failed"
          value={devicesFailed.length}
          accent={devicesFailed.length > 0 ? 'text-red-400' : 'text-gray-500'}
        />
        <MetricCard
          label="Up to Date"
          value={devicesUpToDate.length}
          accent="text-emerald-400"
        />
      </div>

      {/* Stuck jobs banner */}
      {stuckJobs.length > 0 && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-lg bg-orange-500/10 border border-orange-500/20 text-sm text-orange-300">
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
          <span>
            {stuckJobs.length === 1
              ? '1 deployment job has had no progress in 15+ minutes.'
              : `${stuckJobs.length} deployment jobs have had no progress in 15+ minutes.`}
            {' '}Review the job status below and cancel if needed.
          </span>
        </div>
      )}

      {/* Job cancel error banner */}
      {cancelJobError && (
        <div className="mb-4 flex items-start justify-between gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
          <div className="flex items-start gap-2">
            <XCircle size={15} className="mt-0.5 flex-shrink-0" />
            <span>Cancel failed: {cancelJobError}</span>
          </div>
          <button
            onClick={() => setCancelJobError(null)}
            className="text-red-500 hover:text-red-300 flex-shrink-0 text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Section 1: Firmware Deployments */}
      <div className="card overflow-hidden p-0 overflow-x-auto mb-6">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-3 text-sm font-medium text-white">
          <Download size={16} className="text-kinetica-400" />
          Firmware Deployments
        </div>

        {loadingJobs ? (
          <table className="w-full">
            <tbody>
              <TableSkeleton columns={7} rows={4} />
            </tbody>
          </table>
        ) : jobsList.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No firmware deployments</p>
            <p className="text-xs text-gray-600 mt-1">
              OTA jobs targeting KineticaArm devices will appear here.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Job ID</th>
                <th className="table-header table-cell text-left">Description</th>
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-left">Type</th>
                <th className="table-header table-cell text-right">Progress</th>
                <th className="table-header table-cell text-right">Created</th>
                {canCancel && <th className="table-header table-cell" />}
              </tr>
            </thead>
            <tbody>
              {jobsList.map((job) => {
                const stuck = job.status === 'IN_PROGRESS' && isStuck(job.last_updated_at);
                return (
                  <tr
                    key={job.job_id}
                    className={`border-b border-surface-3 last:border-0 ${stuck ? 'bg-orange-500/5' : ''}`}
                  >
                    <td className="table-cell font-mono text-xs text-kinetica-400">
                      {job.job_id}
                    </td>
                    <td className="table-cell text-gray-200 text-sm">
                      {job.description || <span className="text-gray-600 italic">No description</span>}
                    </td>
                    <td className="table-cell">{jobStatusBadge(job)}</td>
                    <td className="table-cell text-xs text-gray-400 capitalize">
                      {job.target_selection.toLowerCase()}
                    </td>
                    <td className="table-cell text-right">
                      <div className="text-xs text-gray-300 font-mono">
                        {job.succeeded}/{job.targets}
                      </div>
                      {job.targets > 0 && (
                        <div className="w-20 h-1.5 bg-surface-3 rounded-full mt-1 ml-auto overflow-hidden">
                          <div
                            className="h-full bg-emerald-400 rounded-full transition-all"
                            style={{ width: `${Math.round((job.succeeded / job.targets) * 100)}%` }}
                          />
                        </div>
                      )}
                      {job.failed > 0 && (
                        <div className="text-[10px] text-red-400 mt-0.5">
                          {job.failed} failed
                        </div>
                      )}
                      {job.queued > 0 && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {job.queued} queued
                        </div>
                      )}
                    </td>
                    <td className="table-cell text-right text-xs text-gray-500">
                      {job.created_at
                        ? new Date(job.created_at).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '--'}
                    </td>
                    {canCancel && (
                      <td className="table-cell text-right">
                        {job.status === 'IN_PROGRESS' && (
                          <button
                            className="text-[11px] text-gray-500 hover:text-red-400 transition-colors flex items-center gap-1 ml-auto"
                            disabled={cancellingJob === job.job_id || cancelJobMutation.isPending}
                            onClick={() => {
                              setCancelJobError(null);
                              setCancellingJob(job.job_id);
                              cancelJobMutation.mutate(job.job_id);
                            }}
                          >
                            {cancellingJob === job.job_id ? (
                              <Loader size={10} className="animate-spin" />
                            ) : (
                              <Ban size={10} />
                            )}
                            Cancel
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Device cancel error banner */}
      {cancelDeviceError && (
        <div className="mb-4 flex items-start justify-between gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
          <div className="flex items-start gap-2">
            <XCircle size={15} className="mt-0.5 flex-shrink-0" />
            <span>Cancel failed: {cancelDeviceError}</span>
          </div>
          <button
            onClick={() => setCancelDeviceError(null)}
            className="text-red-500 hover:text-red-300 flex-shrink-0 text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Section 2: Fleet Firmware Status */}
      <div className="card overflow-hidden p-0 overflow-x-auto">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-3 text-sm font-medium text-white">
          <Package size={16} className="text-emerald-400" />
          Fleet Firmware Status
        </div>

        {loadingStatus ? (
          <table className="w-full">
            <tbody>
              <TableSkeleton columns={12} rows={4} />
            </tbody>
          </table>
        ) : deviceStatusList.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No devices found</p>
            <p className="text-xs text-gray-600 mt-1">
              {user?.org_type === 'client'
                ? 'Contact your integrator to commission devices.'
                : 'Commission devices to organisations to see firmware status.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Serial</th>
                <th className="table-header table-cell text-left">Name</th>
                <th className="table-header table-cell text-left">Current Firmware</th>
                <th className="table-header table-cell text-left">Target</th>
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-right">Last Updated</th>
                {canCancel && <th className="table-header table-cell" />}
              </tr>
            </thead>
            <tbody>
              {deviceStatusList.map((device) => {
                const hint = remediationHint(device);
                const isActionable = canCancel && (
                  device.execution_status === 'IN_PROGRESS' ||
                  device.execution_status === 'QUEUED' ||
                  device.execution_status === 'FAILED' ||
                  device.execution_status === 'TIMED_OUT' ||
                  device.execution_status === 'REJECTED'
                );
                const isError = device.execution_status === 'FAILED' ||
                  device.execution_status === 'TIMED_OUT' ||
                  device.execution_status === 'REJECTED';

                return (
                  <tr
                    key={device.serial}
                    className={`border-b border-surface-3 last:border-0 ${isError ? 'bg-red-500/5' : ''}`}
                  >
                    <td className="table-cell font-mono text-xs text-kinetica-400">
                      {device.serial}
                    </td>
                    <td className="table-cell text-gray-200">
                      <div className="flex items-center gap-1.5">
                        {device.is_online === false && (
                          <WifiOff size={11} className="text-gray-600 flex-shrink-0" aria-label="Offline" />
                        )}
                        {device.name}
                      </div>
                    </td>
                    <td className="table-cell font-mono text-xs text-gray-500">
                      v{device.current_firmware}
                    </td>
                    <td className="table-cell font-mono text-xs">
                      {device.target_firmware ? (
                        <span className="text-amber-400">v{device.target_firmware}</span>
                      ) : (
                        <span className="text-gray-600">--</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <div>{executionStatusBadge(device)}</div>
                      {device.failure_reason && (
                        <div className="text-[10px] text-red-400 mt-1 max-w-[200px] leading-snug">
                          {device.failure_reason}
                        </div>
                      )}
                      {hint && (
                        <div className="text-[10px] text-gray-500 mt-1 max-w-[220px] leading-snug">
                          {hint}
                        </div>
                      )}
                    </td>
                    <td className="table-cell text-right text-xs text-gray-500">
                      {device.last_updated
                        ? new Date(device.last_updated).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '--'}
                    </td>
                    {canCancel && (
                      <td className="table-cell text-right">
                        {isActionable && (
                          <button
                            className="text-[11px] text-gray-500 hover:text-red-400 transition-colors flex items-center gap-1 ml-auto"
                            disabled={cancellingDevice === device.serial || cancelDeviceMutation.isPending}
                            onClick={() => {
                              setCancelDeviceError(null);
                              setCancellingDevice(device.serial);
                              cancelDeviceMutation.mutate(device.serial);
                            }}
                          >
                            {cancellingDevice === device.serial ? (
                              <Loader size={10} className="animate-spin" />
                            ) : (
                              <Ban size={10} />
                            )}
                            Cancel
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Info note */}
      <div className="mt-4 flex items-start gap-2 text-[11px] text-gray-600">
        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
        <span>
          OTA updates are managed through AWS IoT Jobs. Cancel actions stop the deployment for
          the selected job or device. A new deployment must be triggered from AWS IoT to retry.
        </span>
      </div>
    </div>
  );
}
